import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  CallMade as SentIcon,
  CallReceived as ReceivedIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  MailOutline as MailOutlineIcon,
  Refresh as RefreshIcon,
  Reply as ReplyIcon,
  Visibility as VisibilityIcon,
} from "@material-ui/icons";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { getMailConfig } from "../../../../api/mailConfig";
import {
  classifyMailboxMessage,
  downloadMailboxAttachment,
  getMailboxMessage,
  getMailboxThreads,
  searchMailboxThreads,
  sendMailboxMail,
} from "../../../../api/mailbox";
import MailClassificationDialog from "../../../components/dialogs/MailClassificationDialog";
import MailComposeDialog from "../../../components/dialogs/MailComposeDialog";
import MailViewDialog from "../../../components/dialogs/MailViewDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1
  ).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(
    2,
    "0"
  )}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function escapeHtml(value) {
  return (value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function statusChip(message) {
  if (message?.estado_clasificacion === "Fuera de contexto") {
    return (
      <Chip
        size="small"
        label="Fuera de contexto"
        style={{ backgroundColor: "#FFE2E5", color: "#F64E60" }}
      />
    );
  }

  if (message?.estado_clasificacion === "Pendiente de clasificar") {
    return (
      <Chip
        size="small"
        label="Pendiente"
        style={{ backgroundColor: "#FFF4DE", color: "#FFA800" }}
      />
    );
  }

  return null;
}

function isSentMessage(message) {
  return (message?.tipo_mail || "").toLowerCase() === "enviado";
}

function getDirectionMeta(message) {
  return isSentMessage(message)
    ? {
        label: "Enviado",
        icon: <SentIcon style={{ fontSize: 18 }} />,
        color: "#0BB783",
        backgroundColor: "#E8FFF3",
      }
    : {
        label: "Recibido",
        icon: <ReceivedIcon style={{ fontSize: 18 }} />,
        color: "#3699FF",
        backgroundColor: "#EAF4FF",
      };
}

function renderPartyLine(label, value) {
  return (
    <div style={{ color: "#7E8299", fontSize: "0.9rem", lineHeight: 1.5 }}>
      <span style={{ fontWeight: 600, color: "#5E6278" }}>{label}: </span>
      <span>{value || "-"}</span>
    </div>
  );
}

function toReplyHtmlBody(mail) {
  const htmlBody = (mail?.cuerpo_html || "").trim();
  if (htmlBody) {
    return htmlBody
      .replace(/<img[^>]+src=["']cid:[^"']*["'][^>]*>/gi, "")
      .replace(/<source[^>]+src=["']cid:[^"']*["'][^>]*>/gi, "")
      .replace(/<a([^>]+)href=["']cid:[^"']*["']([^>]*)>/gi, "<span$1$2>")
      .replace(/<\/a>/gi, "</span>");
  }

  const text = (mail?.cuerpo_texto || "").trim();
  return text ? `<p>${escapeHtml(text).replace(/\r?\n/g, "</p><p>")}</p>` : "";
}

function buildQuotedHistoryHtml(mail) {
  const originalHtml = toReplyHtmlBody(mail);
  const replyDate = formatDate(mail?.fecha_mail);
  const from = mail?.remitente || "-";
  const to = mail?.destinatarios || "-";
  const subject = mail?.asunto || "-";

  return [
    "<p>&nbsp;</p>",
    "<p>----- Mensaje original -----</p>",
    `<p><strong>De:</strong> ${escapeHtml(from)}</p>`,
    `<p><strong>Enviado:</strong> ${escapeHtml(replyDate)}</p>`,
    `<p><strong>Para:</strong> ${escapeHtml(to)}</p>`,
    `<p><strong>Asunto:</strong> ${escapeHtml(subject)}</p>`,
    originalHtml || "<p></p>",
  ].join("");
}

export default function TestMailPage() {
  const [threads, setThreads] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [mailConfig, setMailConfig] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [composeDialog, setComposeDialog] = useState({
    open: false,
    title: "Nuevo mail",
    confirmLabel: "Enviar",
    initialValues: null,
    quotedHistoryHtml: "",
    payload: null,
    lockToEmail: false,
  });
  const [sendingMail, setSendingMail] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMail, setSelectedMail] = useState(null);
  const [classificationDialogOpen, setClassificationDialogOpen] = useState(false);
  const [classifyingMailId, setClassifyingMailId] = useState(null);
  const [threadSearchResults, setThreadSearchResults] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [classificationLoading, setClassificationLoading] = useState(false);
  const [downloadingAttachmentKey, setDownloadingAttachmentKey] = useState(null);
  const mountedRef = useRef(false);

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const accessToken = loggedUser?.accessToken;

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  const loadThreads = useCallback(
    (keepSkeleton = false) => {
      if (mountedRef.current) {
        setLoadingThreads(true);
      }

      return getMailboxThreads(accessToken)
        .then((res) => {
          if (mountedRef.current && res.status === 200) {
            setThreads(res.data || []);
          }
        })
        .catch((error) => {
          if (mountedRef.current) {
            alertError({
              error,
              customMessage: "No se ha podido obtener el buzón de mail.",
            });
          }
        })
        .finally(() => {
          if (mountedRef.current) {
            setLoadingThreads(false);
          }

          if (!keepSkeleton && mountedRef.current) {
            disableLoadingData();
          }
        });
    },
    [accessToken, disableLoadingData]
  );

  useEffect(() => {
    mountedRef.current = true;

    getMailConfig(accessToken)
      .then((res) => {
        if (mountedRef.current && res.status === 200) {
          setMailConfig(res.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        loadThreads();
      });

    return () => {
      mountedRef.current = false;
    };
  }, [accessToken, loadThreads]);

  const handleOpenNewMail = () => {
    setComposeDialog({
      open: true,
      title: "Nuevo mail",
      confirmLabel: "Enviar",
      initialValues: {
        to_email: "",
        subject: "",
        body: "",
      },
      quotedHistoryHtml: "",
      payload: {},
      lockToEmail: false,
    });
  };

  const handleReply = (message) => {
    getMailboxMessage(message.id, accessToken)
      .then((res) => {
        if (mountedRef.current && res.status === 200) {
          const fullMail = res.data;

          setComposeDialog({
            open: true,
            title: "Responder mail",
            confirmLabel: "Enviar respuesta",
            initialValues: {
              to_email: fullMail?.remitente_email || message?.remitente_email || "",
              subject: fullMail?.asunto || message?.asunto || "",
              body: "",
            },
            quotedHistoryHtml: buildQuotedHistoryHtml(fullMail),
            payload: {
              mail_thread_id: fullMail?.mail_thread_id || message?.mail_thread_id || null,
              reply_to_mail_id: fullMail?.id || message?.id,
              incluir_adjuntos_respuesta: true,
              historico_html: buildQuotedHistoryHtml(fullMail),
            },
            lockToEmail: false,
          });
        }
      })
      .catch((error) => {
        if (mountedRef.current) {
          alertError({
            error,
            customMessage: "No se ha podido preparar la respuesta del mail.",
          });
        }
      });
  };

  const handleSendMail = (values) => {
    setSendingMail(true);

    sendMailboxMail(
      {
        ...composeDialog.payload,
        ...values,
      },
      accessToken
    )
      .then((res) => {
        if (mountedRef.current && res.status === 200) {
          alertSuccess({
            title: "Mail enviado",
            customMessage:
              res.data?.message || "El mail se ha enviado correctamente.",
          });
          setComposeDialog((current) => ({ ...current, open: false }));
          loadThreads(true);
        }
      })
      .catch((error) => {
        if (mountedRef.current) {
          alertError({
            error,
            customMessage: "No se ha podido enviar el mail.",
          });
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setSendingMail(false);
        }
      });
  };

  const handleViewMail = (mailId) => {
    getMailboxMessage(mailId, accessToken)
      .then((res) => {
        if (mountedRef.current && res.status === 200) {
          setSelectedMail(res.data);
          setViewDialogOpen(true);
        }
      })
      .catch((error) => {
        if (mountedRef.current) {
          alertError({
            error,
            customMessage: "No se ha podido obtener el detalle del mail.",
          });
        }
      });
  };

  const handleDownloadAttachment = (mailId, attachment) => {
    const downloadKey = `${mailId}-${attachment.index}`;
    setDownloadingAttachmentKey(downloadKey);

    downloadMailboxAttachment(mailId, attachment.index, accessToken)
      .then((res) => {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || attachment.content_type,
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = attachment.file_name || `adjunto-${attachment.index + 1}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        if (mountedRef.current) {
          alertError({
            error,
            customMessage: "No se ha podido descargar el adjunto.",
          });
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setDownloadingAttachmentKey(null);
        }
      });
  };

  const handleOpenClassifyDialog = (mailId) => {
    setClassifyingMailId(mailId);
    setSelectedThreadId(null);
    setClassificationDialogOpen(true);
  };

  const handleSearchThreads = useCallback(
    (query) => {
      searchMailboxThreads(query, accessToken)
        .then((res) => {
          if (mountedRef.current && res.status === 200) {
            setThreadSearchResults(res.data || []);
          }
        })
        .catch((error) => {
          if (mountedRef.current) {
            alertError({
              error,
              customMessage: "No se ha podido buscar threads.",
            });
          }
        });
    },
    [accessToken]
  );

  const handleDiscardPendingMail = () => {
    if (!classifyingMailId) {
      return;
    }

    setClassificationLoading(true);
    classifyMailboxMessage(
      classifyingMailId,
      { action: "discard" },
      accessToken
    )
      .then((res) => {
        if (mountedRef.current && res.status === 204) {
          alertSuccess({
            title: "Mail descartado",
            customMessage: "El mail se ha marcado como fuera de contexto.",
          });
          setClassificationDialogOpen(false);
          loadThreads(true);
        }
      })
      .catch((error) => {
        if (mountedRef.current) {
          alertError({
            error,
            customMessage: "No se ha podido descartar el mail.",
          });
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setClassificationLoading(false);
        }
      });
  };

  const handleAssignPendingMail = () => {
    if (!classifyingMailId || !selectedThreadId) {
      return;
    }

    setClassificationLoading(true);
    classifyMailboxMessage(
      classifyingMailId,
      {
        action: "assign",
        mail_thread_id: selectedThreadId,
      },
      accessToken
    )
      .then((res) => {
        if (mountedRef.current && res.status === 204) {
          alertSuccess({
            title: "Mail asignado",
            customMessage: "El mail se ha asignado al thread seleccionado.",
          });
          setClassificationDialogOpen(false);
          loadThreads(true);
        }
      })
      .catch((error) => {
        if (mountedRef.current) {
          alertError({
            error,
            customMessage: "No se ha podido asignar el mail al thread.",
          });
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setClassificationLoading(false);
        }
      });
  };

  const renderedThreads = useMemo(() => threads || [], [threads]);

  if (isLoadingData) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title="Test Mail">
          <CardHeaderToolbar>
            <Button
              variant="outlined"
              style={{ marginRight: "12px" }}
              onClick={() => loadThreads(true)}
              disabled={loadingThreads}
              startIcon={
                loadingThreads ? <CircularProgress size={16} /> : <RefreshIcon />
              }
            >
              Actualizar
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOpenNewMail}
              startIcon={<MailOutlineIcon />}
            >
              Nuevo mail
            </Button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          {mailConfig?.test_mode && (
            <div
              className="mb-8 p-4"
              style={{
                backgroundColor: "#FFF4DE",
                borderRadius: "0.42rem",
                color: "#7E8299",
              }}
            >
              {mailConfig?.test_email?.trim() ? (
                <>
                  Modo test activo. Todos los envíos irán a{" "}
                  <strong>{mailConfig.test_email}</strong>.
                </>
              ) : (
                <>Modo test activo. Los envíos usarán el email de test configurado.</>
              )}
            </div>
          )}

          <div className="d-flex flex-column" style={{ gap: "16px" }}>
            {!renderedThreads.length && (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  minHeight: "180px",
                  backgroundColor: "#F3F6F9",
                  borderRadius: "0.42rem",
                  color: "#7E8299",
                }}
              >
                No hay mails para mostrar.
              </div>
            )}

            {renderedThreads.map((thread, index) => {
              const threadKey =
                thread.mail_thread_id || `pending-${thread.ultimo_mensaje?.id || index}`;
              const latestMessage = thread.ultimo_mensaje;
              const isExpanded = !!expandedItems[threadKey];
              const visibleMessages = isExpanded
                ? thread.mensajes
                : thread.mensajes.slice(0, 1);

              return (
                <div
                  key={threadKey}
                  style={{
                    border: "1px solid #E4E6EF",
                    borderRadius: "0.42rem",
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-between px-6 py-4"
                    style={{ backgroundColor: "#F3F6F9" }}
                  >
                    <div>
                      <div className="font-weight-bold" style={{ color: "#181C32" }}>
                        {thread.asunto || `Mail ${latestMessage?.id || ""}`}
                      </div>
                      <div style={{ color: "#7E8299", fontSize: "0.95rem" }}>
                        {thread.mail_thread_id
                          ? `Thread ${thread.mail_thread_id}`
                          : `Mail ${latestMessage?.id || ""}`}
                      </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                      {statusChip(latestMessage)}
                      {thread.mensajes.length > 1 && (
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() =>
                            setExpandedItems((current) => ({
                              ...current,
                              [threadKey]: !current[threadKey],
                            }))
                          }
                          startIcon={
                            isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                          }
                        >
                          {isExpanded
                            ? "Ocultar"
                            : `Ver ${thread.mensajes.length} mails`}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div>
                    {visibleMessages.map((message, messageIndex) => {
                      const isLatestMessage = messageIndex === 0;
                      const direction = getDirectionMeta(message);
                      const sent = isSentMessage(message);

                      return (
                        <div
                          key={message.id}
                          className="d-flex align-items-center justify-content-between px-6 py-4"
                          style={{
                            borderTop: "1px solid #E4E6EF",
                            backgroundColor: isLatestMessage ? "#FFFFFF" : "#FAFBFD",
                            borderLeft: `4px solid ${direction.color}`,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0, paddingRight: "16px" }}>
                            <div
                              className="d-flex align-items-center flex-wrap"
                              style={{ gap: "12px", marginBottom: "10px" }}
                            >
                              <div
                                className="d-flex align-items-center"
                                style={{ gap: "10px" }}
                              >
                                <Avatar
                                  style={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: direction.backgroundColor,
                                    color: direction.color,
                                  }}
                                >
                                  {direction.icon}
                                </Avatar>
                                <div>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      color: "#181C32",
                                      fontSize: "0.95rem",
                                    }}
                                  >
                                    {direction.label}
                                  </div>
                                  <div style={{ color: "#7E8299", fontSize: "0.82rem" }}>
                                    Mail {message.id}
                                  </div>
                                </div>
                              </div>

                              <Chip
                                size="small"
                                label={direction.label}
                                style={{
                                  backgroundColor: direction.backgroundColor,
                                  color: direction.color,
                                  fontWeight: 600,
                                }}
                              />
                            </div>

                            <div
                              className="font-weight-bold"
                              style={{
                                color: "#181C32",
                                marginBottom: "8px",
                                fontSize: "1rem",
                              }}
                            >
                              {message.asunto || "-"}
                            </div>

                            <div style={{ marginBottom: "6px" }}>
                              {renderPartyLine("Remitente", message.remitente || "-")}
                              {renderPartyLine(
                                "Destinatario",
                                sent
                                  ? mailConfig?.test_mode && mailConfig?.test_email?.trim()
                                    ? `${message.destinatarios || "-"} (envío real a ${mailConfig.test_email})`
                                    : message.destinatarios || "-"
                                  : message.destinatarios || mailConfig?.email || "-"
                              )}
                            </div>

                            <div style={{ color: "#7E8299", fontSize: "0.9rem" }}>
                              {formatDate(message.fecha_mail)}
                            </div>
                          </div>

                          <div className="d-flex align-items-center" style={{ gap: "4px" }}>
                            <Tooltip title="Ver">
                              <IconButton onClick={() => handleViewMail(message.id)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {isLatestMessage &&
                              message.estado_clasificacion !== "Fuera de contexto" && (
                                <Tooltip title="Responder">
                                  <IconButton onClick={() => handleReply(message)}>
                                    <ReplyIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            {isLatestMessage &&
                              message.estado_clasificacion ===
                                "Pendiente de clasificar" && (
                                <Tooltip title="Clasificar">
                                  <IconButton
                                    onClick={() => handleOpenClassifyDialog(message.id)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      <MailComposeDialog
        open={composeDialog.open}
        title={composeDialog.title}
        confirmLabel={composeDialog.confirmLabel}
        onClose={() =>
          setComposeDialog((current) => ({ ...current, open: false }))
        }
        onSubmit={handleSendMail}
        loading={sendingMail}
        initialValues={composeDialog.initialValues}
        quotedHistoryHtml={composeDialog.quotedHistoryHtml}
        helperText={
          mailConfig?.test_mode
            ? mailConfig?.test_email?.trim()
              ? `Modo test activo: la API enviará siempre a ${mailConfig.test_email}.`
              : "Modo test activo: la API usará el email de test configurado."
            : ""
        }
        lockToEmail={composeDialog.lockToEmail}
      />

      <MailViewDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        mail={selectedMail}
        onDownloadAttachment={handleDownloadAttachment}
        downloadingAttachmentKey={downloadingAttachmentKey}
      />

      <MailClassificationDialog
        open={classificationDialogOpen}
        onClose={() => setClassificationDialogOpen(false)}
        onSearch={handleSearchThreads}
        searchResults={threadSearchResults}
        selectedThreadId={selectedThreadId}
        onSelectThread={setSelectedThreadId}
        onDiscard={handleDiscardPendingMail}
        onAssign={handleAssignPendingMail}
        loading={classificationLoading}
      />
    </>
  );
}
